import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL, ENDPOINTS } from '../../constants/api';
import { decodeToken, getToken } from '../../utils/TokenUtils';
import { useParams } from 'react-router-dom';

const ApiDocumentation = () => {
  const { link_id } = useParams();
  const [apiData, setApiData] = useState(null);
  const [error, setError] = useState(null);
  const token = getToken();
  const companyName = decodeToken(token, 'companyName');
  const logo = decodeToken(token, 'logo');

  useEffect(() => {
    if (!link_id) return;

    axios
      .get(`${API_URL}/${ENDPOINTS.SYS}/api-documentation/${link_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => setApiData(res.data))
      .catch((err) => {
        console.error(err);
        setError('Failed to load API data.');
      });
  }, [link_id]);

  const getMethod = (form_type) =>
    ['report', 'invoice-route'].includes(form_type) ? 'GET' : 'POST';

  const getResponseExamples = (formType) => {
    if (formType.includes('modal') || formType.includes('form')) {
      return {
        success: {
          message: 'Form submitted successfully',
          success: 1,
          status: true,
        },
        duplicate: {
          message: 'Duplicate entry found',
          success: 0,
          status: false,
        },
        error: {
          message: 'An unexpected error occurred',
          success: 0,
          status: false,
        },
      };
    } else if (formType === 'report') {
      return {
        success: {
          success: 10,
          status: true,
          result: [{ id: 1, name: 'Sample report item' }],
        },
        error: {
          success: 0,
          status: false,
          message: 'Failed to retrieve report data',
        },
      };
    } else {
      return {
        error: {
          message: 'Unknown response type',
        },
      };
    }
  };

  if (error) return <p style={{ color: 'red' }}>{error}</p>;
  if (!apiData) return <p>Loading documentation...</p>;

  const { link, inputs } = apiData;
  const endpoint = link[0];
  const method = getMethod(endpoint.form_type);
  const responses = getResponseExamples(endpoint.form_type);

  return (
    <div
      style={{
        fontFamily: 'Arial, sans-serif',
        maxWidth: '900px',
        margin: 'auto',
      }}
    >
      {/* Header */}
      <header
        style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}
      >
        <img
          src={`${API_URL}/${logo}`}
          alt="Company Logo"
          style={{ width: 60, marginRight: 20 }}
        />
        <h1>{companyName}</h1>
      </header>
      <h3 className="text-center"> {endpoint.name} API Documentation</h3>

      {/* Endpoint Info */}
      <section>
        <h2 style={{ marginBottom: 0 }}>{endpoint.name}</h2>
        <p style={{ marginTop: 4 }}>
          <strong>Endpoint:</strong>{' '}
          <code>
            {API_URL}/api/sys/create/{endpoint.route}
          </code>
        </p>
        <p>
          <strong>Method:</strong> {method}
        </p>
        <p>
          <strong>Authorization:</strong> Bearer Token (JWT)
        </p>
      </section>

      {/* Parameters */}
      <section>
        <h3>Parameters</h3>
        <table
          style={{
            borderCollapse: 'collapse',
            width: '100%',
            marginBottom: '20px',
          }}
        >
          <thead>
            <tr>
              <th style={tableHeaderStyle}>Name</th>
              <th style={tableHeaderStyle}>Data Type</th>
              <th style={tableHeaderStyle}>Required</th>
            </tr>
          </thead>
          <tbody>
            {inputs.map((input, idx) => (
              <tr key={idx}>
                <td style={tableCellStyle}>{input.name}</td>
                <td style={tableCellStyle}>
                  {input.datatype
                    .replace('varchar', 'string')
                    .replace('text', 'string') || 'N/A'}
                </td>
                <td style={tableCellStyle}>
                  {input.is_required === 'required' ? 'Yes' : 'No'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Sample Request */}
      <section>
        <h3>API Request</h3>
        <pre style={codeBlockStyle}>
          {`${method} ${API_URL}/api/sys/create/${endpoint.route}
Headers:
  Authorization: Bearer YOUR_JWT_TOKEN
  Content-Type: application/json

Body:
{
${inputs
  .filter((i) => i.is_required === 'required')
  .map(
    (i) =>
      `  "${i.name}": "<${i.datatype.replace('varchar', 'string').replace('text', 'string') || 'value'}>"`
  )
  .join(',\n')}
}
`}
        </pre>
      </section>

      {/* Sample Responses */}
      <section>
        <h3>API Responses</h3>

        {Object.entries(responses).map(([key, response]) => (
          <div key={key}>
            <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong>
            <pre style={codeBlockStyle}>
              {JSON.stringify(response, null, 2)}
            </pre>
          </div>
        ))}
      </section>

      {/* Footer */}
      <footer style={{ marginTop: 40, fontSize: '0.9em', color: '#777' }}>
        &copy; {new Date().getFullYear()} {companyName} — All Rights Reserved
      </footer>
    </div>
  );
};

// Styles
const tableHeaderStyle = {
  border: '1px solid #ccc',
  backgroundColor: '#f0f0f0',
  padding: '8px',
  textAlign: 'left',
};

const tableCellStyle = {
  border: '1px solid #ccc',
  padding: '8px',
};

const codeBlockStyle = {
  background: '#f7f7f7',
  padding: 10,
  overflowX: 'auto',
  whiteSpace: 'pre-wrap',
  marginBottom: '20px',
};

export default ApiDocumentation;
